// PDP web components

// AJAX add-to-cart, ported from the app's CartForm.svelte: intercept the
// native product form, add via /cart/add.js, re-render the cart drawer via
// the Section Rendering API and slide it open (the app pushes the /cart
// modal state after LinesAdd). The listener lives on document because
// variant-picker.js swaps `.product__action-row` (which holds the button)
// on every variant change — the form node itself is never replaced.
document.addEventListener('submit', async (event) => {
	const form = event.target.closest('#product-form');
	if (!form) return;
	event.preventDefault();
	const btn = form.querySelector('.product__atc[data-shoppable]');
	if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
	const drawer = document.querySelector('cart-drawer');
	const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
	btn.classList.add('is-loading');
	btn.disabled = true;
	try {
		const body = new FormData(form);
		if (drawer?.dataset.sectionId) {
			body.append('sections', drawer.dataset.sectionId);
			body.append('sections_url', window.location.pathname);
		}
		const res = await fetch(`${root}cart/add.js`, {
			method: 'POST',
			headers: { Accept: 'application/json' },
			body,
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.description || data.message || 'Add to cart failed');
		// /cart/add.js returns the added line, not the cart — fetch the cart
		// for the fresh item_count, then reuse the drawer's own render().
		const cart = await (await fetch(`${root}cart.js`)).json();
		if (drawer) {
			drawer.render(
				{ sections: data.sections, item_count: cart.item_count },
				drawer.dataset.sectionId
			);
			drawer.open();
		}
		// Added affordance: swap the label for a checkmark, same as the app's
		// AddToCart "clicked" state, then restore.
		const label = btn.querySelector('span');
		if (label) {
			const original = label.innerHTML;
			label.innerHTML =
				'<svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true"><path d="M1 7.5L6.5 13L17 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
			setTimeout(() => {
				label.innerHTML = original;
			}, 2000);
		}
	} catch (error) {
		console.error('[pdp] add to cart failed', error);
	} finally {
		btn.classList.remove('is-loading');
		btn.disabled = false;
	}
});

class QuantityInput extends HTMLElement {
	connectedCallback() {
		this.input = this.querySelector('input');
		this.dec = this.querySelector('[data-qty-dec]');
		this.inc = this.querySelector('[data-qty-inc]');
		this.max = Number(this.dataset.max) || 99;
		// A disabled input means "no variant selected yet" — the whole stepper
		// stays inert until variant-picker swaps in an enabled one.
		if (this.input?.disabled) return;
		this.dec?.addEventListener('click', () => this.step(-1));
		this.inc?.addEventListener('click', () => this.step(1));
		this.updateButtons();
	}
	step(d) {
		const next = Math.min(this.max, Math.max(1, Number(this.input.value || 1) + d));
		this.input.value = next;
		this.updateButtons();
	}
	updateButtons() {
		const value = Number(this.input.value || 1);
		if (this.dec) this.dec.disabled = value <= 1;
		if (this.inc) this.inc.disabled = value >= this.max;
	}
}
customElements.define('quantity-input', QuantityInput);

/* Notify-me state is managed by the favorites-drawer section
   (theme/sections/favorites-drawer.liquid), same component as favorites. */

class ShoppableGate extends HTMLElement {
	connectedCallback() {
		const active = this.dataset.activeCollection || '';
		const ref = new URLSearchParams(location.search).get('ref')
			|| (document.referrer.match(/\/collections\/([^/?#]+)/)?.[1] ?? '');
		if (ref && active && ref !== active) {
			this.setAttribute('data-blocked', '');
			const shoppableBtn = this.querySelector('[data-shoppable]');
			shoppableBtn?.setAttribute('hidden', '');
			shoppableBtn?.setAttribute('disabled', '');
			this.querySelector('.product__notify')?.removeAttribute('hidden');
			// Quantity lives in the controls row above, not inside the gate — a
			// downgrade to Notify-me must hide it too, same as when the
			// product isn't shoppable server-side.
			this.closest('.product__form')?.querySelector('.product__qty')?.setAttribute('hidden', '');
		}
	}
}
customElements.define('shoppable-gate', ShoppableGate);

class PdpAccordion extends HTMLElement {
	connectedCallback() {
		this.btn = this.querySelector('.product__acc-btn');
		this.btn?.addEventListener('click', () => {
			const open = this.toggleAttribute('open');
			this.btn.setAttribute('aria-expanded', String(open));
		});
	}
}
customElements.define('pdp-accordion', PdpAccordion);

class PdpRecommendations extends HTMLElement {
	async connectedCallback() {
		const url = this.dataset.url;
		if (!url) return;
		try {
			const res = await fetch(url);
			if (!res.ok) return;
			const html = await res.text();
			const doc = new DOMParser().parseFromString(html, 'text/html');
			const section = doc.querySelector('.pdp-related');
			if (section) this.replaceChildren(section);
		} catch {
			// Network/parse failure: leave the host empty, render nothing.
		}
	}
}
customElements.define('pdp-recommendations', PdpRecommendations);

class PdpReadMore extends HTMLElement {
	connectedCallback() {
		this.content = this.querySelector('.product__readmore-content');
		this.toggle = this.querySelector('.product__readmore-toggle');
		if (!this.content || !this.toggle) return;
		this.toggle.addEventListener('click', () => {
			const expanded = this.toggleAttribute('data-expanded');
			this.toggle.textContent = expanded ? 'Read Less' : 'Read More';
			this.toggle.setAttribute('aria-expanded', String(expanded));
		});
		// Same detection as the app: an accordion starts hidden/height:0, so
		// checking overflow at connect time would always read false — observe
		// for layout changes instead of a one-shot check.
		const observer = new ResizeObserver(() => {
			if (this.hasAttribute('data-expanded')) return;
			this.toggle.hidden = this.content.scrollHeight <= this.content.clientHeight;
		});
		observer.observe(this.content);
	}
}
customElements.define('pdp-readmore', PdpReadMore);

// The mobile buy box (picker + stepper + action row) lives after Details in the
// document flow, but remains pinned to the bottom of the viewport until that
// natural position scrolls into view. Its rows are replaced after a variant
// change, so resolve the bar on every update rather than retaining a reference.
class MobileProductActions {
	constructor(root) {
		this.root = root;
		this.mobile = window.matchMedia('(max-width: 767px)');
		this.update = this.update.bind(this);
		this.queueUpdate = this.queueUpdate.bind(this);
		this.observer = new MutationObserver(this.queueUpdate);
		this.resizeObserver = new ResizeObserver(this.queueUpdate);
	}

	connect() {
		this.observer.observe(this.root, { childList: true, subtree: true });
		this.resizeObserver.observe(this.root);
		window.addEventListener('scroll', this.queueUpdate, { passive: true });
		window.addEventListener('resize', this.queueUpdate);
		this.mobile.addEventListener('change', this.queueUpdate);
		this.update();
	}

	queueUpdate() {
		if (this.frame) return;
		this.frame = requestAnimationFrame(() => {
			this.frame = null;
			this.update();
		});
	}

	update() {
		const bar = this.root.querySelector('.product__form-wrap');
		if (!bar) return;

		if (!this.mobile.matches) {
			bar.classList.remove('is-mobile-fixed');
			document.documentElement.style.removeProperty('--pdp-action-bar-h');
			return;
		}

		// Measure the bar as it looks pinned (its padding only exists in that
		// state) and publish that height: the hero caps itself against it so the
		// photo — and the cream panel cap glued to its bottom edge — always end
		// above the bar. One row of controls is 96px tall, two are 160px.
		bar.classList.add('is-mobile-fixed');
		const fixedHeight = bar.getBoundingClientRect().height;
		document.documentElement.style.setProperty('--pdp-action-bar-h', `${Math.round(fixedHeight)}px`);

		// Then measure from the bar's in-flow location and pin it only while that
		// location is below the viewport. Removing and restoring the class in one
		// animation frame prevents the pinned bar from affecting the measure.
		bar.classList.remove('is-mobile-fixed');
		const inFlowTop = bar.getBoundingClientRect().top;
		const barHasReachedItsPlace = inFlowTop <= window.innerHeight - fixedHeight;
		bar.classList.toggle('is-mobile-fixed', !barHasReachedItsPlace);
	}
}

document.querySelectorAll('product-info').forEach((root) => new MobileProductActions(root).connect());
